import { useTranslation } from 'react-i18next';
import { useUsuariosUAU } from '../../../hooks/useUsuariosUAU';

// ===============================
// TIPOS
// ===============================

interface UserSelectorProps {
  selectedUserId: number | null;
  onUserSelect: (userId: number) => void;
  onUserDeselect: () => void;
}

// ===============================
// COMPONENTE
// ===============================

export function UserSelector({ selectedUserId, onUserSelect, onUserDeselect }: UserSelectorProps) {
  const { t } = useTranslation();
  const { getUsuariosActivos, isLoading, error } = useUsuariosUAU();
  
  const usuariosActivos = getUsuariosActivos();

  if (isLoading) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="flex items-center justify-center py-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
            <p className="text-gray-600">{t('common.loading')}</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
        <div className="text-center py-8">
          <div className="mb-4">
            <svg className="mx-auto h-12 w-12 text-red-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {t('errors.load_error')}
          </h3>
          <p className="text-red-600 text-sm max-w-md mx-auto">
            {error}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-medium text-gray-900">
          Seleccionar Usuario
        </h2>
        {selectedUserId && (
          <button
            onClick={onUserDeselect}
            className="text-sm text-gray-500 hover:text-gray-700"
          >
            Deseleccionar
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {usuariosActivos.map((usuario) => {
          const isSelected = selectedUserId === usuario.id;
          
          return (
            <button
              key={usuario.id}
              onClick={() => onUserSelect(usuario.id)}
              className={`
                p-4 rounded-lg border-2 text-left transition-all duration-200
                ${
                  isSelected
                    ? 'border-blue-500 bg-blue-50 shadow-md'
                    : 'border-gray-200 hover:border-blue-300 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-center space-x-3">
                {/* Avatar */}
                <div className={`
                  w-10 h-10 rounded-full flex items-center justify-center font-medium text-sm
                  ${
                    isSelected
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600'
                  }
                `}>
                  {usuario.full_name?.charAt(0) || usuario.username.charAt(0)}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <p className={`
                    font-medium truncate
                    ${
                      isSelected ? 'text-blue-900' : 'text-gray-900'
                    }
                  `}>
                    {usuario.full_name || usuario.username}
                  </p>
                  <p className={`
                    text-xs truncate
                    ${
                      isSelected ? 'text-blue-600' : 'text-gray-500'
                    }
                  `}>
                    {usuario.email}
                  </p>
                  {usuario.tipo_usuario && (
                    <span className={`
                      inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-1
                      ${
                        isSelected
                          ? 'bg-blue-200 text-blue-800'
                          : 'bg-gray-200 text-gray-700'
                      }
                    `}>
                      {usuario.tipo_usuario}
                    </span>
                  )}
                </div>
              </div>

              {/* Indicador de selección */}
              {isSelected && (
                <div className="mt-2 flex items-center justify-center">
                  <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                </div>
              )}
            </button>
          );
        })}
      </div>

      {usuariosActivos.length === 0 && (
        <div className="text-center py-8 text-gray-500">
          <svg className="mx-auto h-12 w-12 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
          </svg>
          <p className="text-sm">No hay usuarios disponibles</p>
        </div>
      )}
    </div>
  );
}
